import { useQuery } from '@tanstack/vue-query';
import type { MaybeRefOrGetter } from 'vue';
import { queries } from '~/keys';
import type { ExclusiveUnitsPayload } from '~/types/exclusives';
import { serverClockOffsetMs } from '~/utils/exclusives/holdCountdown';

// Polling cadence (ID-118 AC 4.5): a unit taken by anyone disappears within 5 s.
export const EXCLUSIVE_UNITS_POLL_MS = 3_000;

type FetchFailure = { statusCode?: number; status?: number; data?: { data?: { reason?: string } } };

const failureStatus = (error: unknown): number | null => {
  const e = error as FetchFailure | null;
  return e?.statusCode ?? e?.status ?? null;
};

const failureReason = (error: unknown): string | null => (error as FetchFailure | null)?.data?.data?.reason ?? null;

// Live unit list: polled only while the tab is visible, refetched on focus, stopped on 401 or when exclusivity ends.
// Keeps the last good payload on transient errors.
export const useExclusiveUnits = (catalogueProjectId: MaybeRefOrGetter<number | null>) => {
  const authStore = useAuthStore();
  const sessionExpired = ref(false);
  const noLongerExclusive = ref(false);
  const accessDenied = ref(false);
  const clockOffsetMs = ref(0);

  const projectId = computed(() => toValue(catalogueProjectId));
  const active = computed(
    () =>
      !!authStore.token &&
      !!projectId.value &&
      !sessionExpired.value &&
      !noLongerExclusive.value &&
      !accessDenied.value,
  );

  const query = useQuery({
    queryKey: computed(() => queries.exclusiveUnits.project(projectId.value ?? 0).queryKey),
    queryFn: async () => {
      const res = await $fetch<{ success: boolean; data: ExclusiveUnitsPayload }>(
        `/api/exclusives/${projectId.value}/units`,
        { headers: { Authorization: `Bearer ${authStore.token}` } },
      );
      clockOffsetMs.value = serverClockOffsetMs(res.data.serverNow, Date.now());
      return res.data;
    },
    enabled: active,
    refetchInterval: computed(() => (active.value ? EXCLUSIVE_UNITS_POLL_MS : false)),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
    staleTime: 0,
    retry: false,
    throwOnError: false,
  });

  watch(query.error, (error) => {
    if (!error) return;
    const status = failureStatus(error);
    if (status === 401) sessionExpired.value = true;
    if (status === 403) accessDenied.value = true;
    if (status === 404 && failureReason(error) === 'not_exclusive') noLongerExclusive.value = true;
  });

  const hasData = computed(() => !!query.data.value);
  // Last good list stays visible (greyed) while a retry is pending.
  const isFailing = computed(() => query.isError.value && active.value);

  const refresh = () => (active.value ? query.refetch() : Promise.resolve());

  return {
    data: query.data,
    isLoading: computed(() => query.isPending.value && active.value),
    hasData,
    isFailing,
    sessionExpired: readonly(sessionExpired),
    noLongerExclusive: readonly(noLongerExclusive),
    accessDenied: readonly(accessDenied),
    clockOffsetMs: readonly(clockOffsetMs),
    refresh,
  };
};
